import { useRef, useEffect } from "react";

/**
 * Tie an async operation to your component's lifecycle.
 * The provided function is called with a utility for checking component mount status.
 *
 * @param {function} asyncRunner An async function, called with a function which tells you if deps are current. You can call this function with another function, which will only be run if conditions are current..
 * @param {Array} dependencies Effect dependencies, passed directly to underlying `useEffect`.
 *
 * @returns {undefined}
 *
 * @example
 *
 * useAsyncEffect(async (getIsCurrent) => {
 *   setIsLoading(true)
 *
 *   let loan = null
 *   let error = null
 *
 *   try {
 *     loan = await getLoan(loanId)
 *   } catch (error) {
 *     error = error
 *   }
 *   if (!getIsCurrent()) return
 *
 *   setLoan(loan)
 *   setError(error)
 *   setIsLoading(false)
 * }, [loanId])
 */

let useAsyncEffect = (
  asyncRunner: (getIsCurrent: (toRun?: () => void) => boolean) => Promise<void>,
  dependencies: any[] = []
): void => {
  let asyncRunnerRef = useRef(asyncRunner);

  useEffect(() => {
    asyncRunnerRef.current = asyncRunner;
  }, [asyncRunner]);

  useEffect(() => {
    let isCurrent = true;
    let getIsCurrent = (toRun?: () => void): boolean => {
      if (isCurrent && typeof toRun === "function") toRun();
      return isCurrent;
    };

    asyncRunnerRef.current(getIsCurrent);

    return () => {
      isCurrent = false;
    };
    // dependencies are passed as a prop so linter will not be able to statically analyze
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);
};

export default useAsyncEffect;
